import React, { useState, useEffect, useContext, useRef, PropsWithChildren, useCallback } from "react";
import classNames from "classnames";
import { SearchAutocomplete, SearchAutocompletePropsNoIcon } from "Components/Autocompletes/SearchAutocomplete";
import { Button } from "Components/ReactstrapSlim";
import * as Icons from "Components/Icons";
import { IconRenderFunction } from "Components/Inputs";
import { SearchAutocompleteClient } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { HomeBaseModal } from "../Main/HomeBaseModal";
import UserContext from "../../UserContext";
import { FieldValues } from "react-hook-form";
import { KnownSearchAutocomplete } from "Models/Search";

export const SearchAutocompleteWithDropdown = function <T extends FieldValues>(props: PropsWithChildren<SearchAutocompletePropsNoIcon<T>>) {
   const searchAutocompleteClient = useApiClient(SearchAutocompleteClient);
   const [options, setOptions] = useState<KnownSearchAutocomplete[]>([]);
   const [homeBaseModalOpened, setHomeBaseModalOpened] = useState(false);
   const isDropDownShownRef = useRef<boolean>(false);
   const dropDownShownOptionsRef = useRef<Parameters<IconRenderFunction<KnownSearchAutocomplete>>[1] | undefined>(undefined);
   const { user, enterpriseUser, refresh: userRefresh } = useContext(UserContext);

   const toggleHomeBaseModal = useCallback(() => {
      setHomeBaseModalOpened((wasOpened) => !wasOpened);
   }, []);

   const loadOptions = async (abortSignal?: AbortSignal) => {
      try {
         setOptions(await searchAutocompleteClient.dropdownAutocomplete(abortSignal) as KnownSearchAutocomplete[]);
      }
      catch { }
   }

   useEffect(() => {
      const abortController = new AbortController();
      loadOptions(abortController.signal);
      return function cleanup() {
         abortController.abort();
      }
   }, []);

   useEffect(() => {
      if (isDropDownShownRef.current && dropDownShownOptionsRef.current) {
         dropDownShownOptionsRef.current(options);
      }
   }, [options])

   const iconRenderer: IconRenderFunction<KnownSearchAutocomplete> = function (isShown, showOptions, hideOptions) {
      isDropDownShownRef.current = isShown;
      dropDownShownOptionsRef.current = showOptions;

      const onClick = (e: React.SyntheticEvent) => {
         e.preventDefault();
         if (isShown) {
            hideOptions();
         }
         else {
            showOptions(options);
         }
      }

      return (<Button color="clean" size="icon" className={classNames("search-autocomplete-dropdown-button", { "search-autocomplete-dropdown-button--toggled": isShown })} onClick={onClick}>
         <Icons.Dropdown />
      </Button>)
   };
   iconRenderer.isIconRenderFunction = true;

   const editHomeBase = useCallback((savedSearchId: Guid) => {
      toggleHomeBaseModal();
   }, []);

   const onHomebaseChanged = useCallback(() => {
      if (!enterpriseUser.homeBaseSearchId)
         userRefresh();

      loadOptions();
   }, [user]);


   return (<>
      <SearchAutocomplete icon={iconRenderer} onEditHomeBase={editHomeBase} {...props} />
      {homeBaseModalOpened && <HomeBaseModal searchId={enterpriseUser.homeBaseSearchId} toggle={toggleHomeBaseModal} isOpen={homeBaseModalOpened} onSuccess={onHomebaseChanged} centered />}
   </>)
}